// Everything on the Finance page that reads a transaction's Title lives here
// (plus isSelfPayroll, whose FIG side now goes by category instead).
//
// Finance_Transactions is seeded from Plaid, so Title is the bank's raw
// descriptor, not the hand-cleaned text of the old CSV seeds. The shapes these
// rules are written against (sampled live, 2026-10-01):
//
//   card purchase   KROGER #387 COLLIERVILLE TN 09/27
//                   RESTAURANT DEPOT MEMPHIS TN 241278 06/05
//                   KROGER #387 240 NEW B COLLIERVILLE TN949201 09/24
//   short ACH       FRAGOS INSURANCE PAYROLL PPD ID: 9111111101
//   long ACH        ORIG CO NAME:ADP Tax ORIG ID:1223006057 DESC DATE:260604
//                   CO ENTRY DESCR:ADP Tax SEC:CCD TRACE#:… IND NAME:FRAGOS
//                   INSURANCE GROUP AC TRN: 1559499219TC
//   online payment  Online Payment 30732234875 To MEMPHIS LIGHT GAS & WATER 09/08
//
// Matching is by whole phrase on purpose. The long ACH rows carry
// "IND NAME:FRAGOS INSURANCE GROUP", so anything looser than the full payroll
// phrase (e.g. "FRAGOS INSURANCE") would also claim ADP, carrier and trust rows.

// Which paycheck an inflow is. FIG payroll has had two descriptors: the ADP
// account was "…DIRECT DEP" (PPD ID 9444444404) and is now "…PAYROLL"
// (PPD ID 9111111101). FedEx payroll is historical only — Marc has left — and
// the match stays so the old rows keep their colour in the trend charts.
export function classifySource(title) {
  const up = (title || '').toUpperCase();
  if (up.includes('FEDERAL EXPRESS PAYROLL')) return 'FedEx';
  if (up.includes('FRAGOS INSURANCE PAYROLL') || up.includes('FRAGOS INSURANCE DIRECT DEP')) return 'FIG';
  return 'Other';
}

// Finance_Categories id of "Payroll (Owner Wages)".
export const OWNER_WAGES_CATEGORY_ID = '39';

// Marc paying himself: FIG's ADP wage debit, and the deposits it becomes in the
// personal account (each wage debit equals that payday's deposits, to the
// cent). Both rows are real on their own tab — an expense for FIG, income for
// Personal — and cancel on the consolidated All view.
// The FIG leg is whatever is filed under Payroll (Owner Wages): the category
// decides, not the "ADP WAGE PAY" descriptor. The personal leg has no category
// of its own (category 1 is every paycheck, FedEx included), so it is still
// told apart by its title.
// ADP TAX and the ADP fees are NOT self-payroll: that money leaves the family.
export function isSelfPayroll(t) {
  if (!t) return false;
  if (t.Context === 'FIG' && t.Direction === 'Out') return String(t.CategoryLookupId) === OWNER_WAGES_CATEGORY_ID;
  if (t.Context === 'Personal' && t.Direction === 'In') return classifySource(t.Title) === 'FIG';
  return false;
}

// Collapses a descriptor to the merchant, so one merchant is one line in the
// category drill-down instead of one line per visit.
export function normalizeMerchant(title) {
  if (!title) return 'Unknown';
  let s = title.trim();

  // Long ACH: the company is the first field. Must run before the '#' split —
  // every one of these rows contains "TRACE#:".
  const ach = s.match(/^ORIG CO NAME:(.+?)\s+ORIG ID:/i);
  if (ach) {
    s = ach[1].trim();
  } else {
    s = s.replace(/\s+\d{2}\/\d{2}$/, '');                 // trailing purchase date " MM/DD"
    s = s.replace(/^Online Payment \d+ To\s+/i, '');       // keep the payee
    s = s.replace(/\s+PPD ID:\s*\d+$/i, '');               // short ACH id
    s = s.split('#')[0].trim();
    s = s.replace(/\s+card\s+\d+/gi, '').trim();
    s = s.replace(/\b\d{5}\b.*/, '').trim();
    s = s.replace(/(^|[^\d\s])\s?\d{6}$/, '$1').trim();    // reference: "TN 241278" or glued "TN949201"
    s = s.replace(/\s+[A-Z]{2}$/, '').trim();
    s = s.replace(/\s{2,}/g, ' ').trim();
  }

  if (!s) return title.trim();
  return s.replace(/\b\w/g, c => c.toUpperCase());
}
